import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, EmptyState } from "../../components/common/Primitives";
import { FileSearch, XCircle, MessageSquare, ChevronDown, ChevronUp, Send, CheckCircle2, Eye, TrendingDown, TrendingUp } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import RejectionReasonModal from "../../components/RejectionReasonModal";

/* ---------- Query Composer + History (nested per startup) ---------- */
function QueryPanel({ interest, bank, onSent }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);

  const toggle = (qid) => setSelected((arr) => arr.includes(qid) ? arr.filter((x) => x !== qid) : [...arr, qid]);
  const send = async () => {
    if (selected.length === 0) return toast.error("Pick at least one question.");
    setSending(true);
    try {
      await api.post("/investor/query", { interest_id: interest.interest_id, question_ids: selected });
      toast.success("Query sent for response.");
      setSelected([]);
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  const queries = interest.queries || [];
  const responded = queries.filter((q) => q.status === "Response Shared" || q.status === "Answered" || (q.responses && q.responses.length));
  const pending = queries.filter((q) => !responded.includes(q));

  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid={`interest-queries-${interest.interest_id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`interest-queries-toggle-${interest.interest_id}`}
      >
        <div className="flex items-center gap-2">
          <MessageSquare size={13} className="text-[#8A6600]" />
          <span className="text-sm font-heading text-ink">Queries & Responses</span>
          <Pill tone={responded.length ? "success" : pending.length ? "warn" : "muted"}>
            {responded.length} answered · {pending.length} pending
          </Pill>
        </div>
        {open ? <ChevronUp size={14} className="text-ink-soft" /> : <ChevronDown size={14} className="text-ink-soft" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`interest-queries-body-${interest.interest_id}`}>
          {/* History */}
          {queries.length === 0 ? (
            <div className="text-xs text-ink-soft italic">No queries sent yet — pick a question below to start the conversation.</div>
          ) : (
            queries.map((q) => (
              <div key={q.query_id} className="border border-navy/10 rounded-md p-3 bg-white/60" data-testid={`query-${q.query_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-soft">
                    Sent {q.created_at ? new Date(q.created_at).toLocaleDateString() : "—"} · {(q.questions || []).length} questions
                  </div>
                  <Pill tone={q.status === "Response Shared" || q.status === "Answered" ? "success" : "warn"}>
                    {q.status || "Pending"}
                  </Pill>
                </div>
                <ol className="text-sm space-y-2 list-decimal ml-4">
                  {(q.questions || []).map((qq, i) => {
                    const answerText = qq.answer || qq.response || (q.responses && q.responses[i]?.answer);
                    return (
                      <li key={qq.question_id || i}>
                        <div className="text-ink">{qq.question || qq.text}</div>
                        {answerText ? (
                          <div className="mt-1.5 pl-3 border-l-2 border-emerald-500/40 bg-emerald-50/40 rounded-r px-3 py-2" data-testid={`query-answer-${q.query_id}-${i}`}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1 flex items-center gap-1"><CheckCircle2 size={10} /> Startup responded</div>
                            <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{answerText}</div>
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-ink-soft italic">Awaiting founder response…</div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))
          )}

          {/* Composer */}
          <div className="border border-dashed border-navy/15 rounded-md p-3" data-testid={`query-composer-${interest.interest_id}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600] mb-2">Send a new query</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
              {bank.map((qq) => {
                const on = selected.includes(qq.question_id);
                return (
                  <label key={qq.question_id} className={`border p-2 text-xs cursor-pointer transition-colors rounded ${on ? "border-[#8A6600] bg-gold/10 text-[#8A6600]" : "border-navy/15 text-ink-soft hover:border-navy/40"}`}>
                    <input type="checkbox" className="hidden" checked={on} onChange={() => toggle(qq.question_id)} data-testid={`q-${interest.interest_id}-${qq.question_id}`} />
                    {qq.question}
                  </label>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Button onClick={send} disabled={sending || selected.length === 0} testId={`send-query-${interest.interest_id}`}>
                <Send size={12} className="inline mr-1" />{sending ? "Sending…" : `Send (${selected.length})`}
              </Button>
              {selected.length > 0 && (
                <button className="text-xs text-ink-soft hover:underline" onClick={() => setSelected([])}>Clear</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Interests tab ---------- */
function InterestsTab({ rows, bank, onNotProceeding, onRefresh }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No interests expressed yet"
        hint="Explore Curated Deals and tap 'Express Interest' on a startup you want to speak to."
      />
    );
  }
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <Card key={r.interest_id} testId={`interest-${r.interest_id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <Link
                to={`/investor/startup/${r.startup_id}`}
                className="font-heading text-ink text-lg hover:text-[#8A6600]"
                data-testid={`interest-link-${r.interest_id}`}
              >{r.startup_name}</Link>
              <div className="text-[11px] font-mono uppercase text-ink-muted tracking-wider mt-1">{r.sector || "—"}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/investor/dd-reports/${r.startup_id}`}
                className="text-xs font-heading text-[#8A6600] hover:underline inline-flex items-center gap-1"
                data-testid={`interest-dd-link-${r.interest_id}`}
              ><FileSearch size={12} /> Diligence Reports</Link>
              <Button
                variant="ghost"
                onClick={() => onNotProceeding(r)}
                data-testid={`interest-not-proceeding-${r.interest_id}`}
                title="Mark as not proceeding + give feedback"
              >
                <XCircle size={12} className="inline mr-1 text-rose-600" /> Not proceeding
              </Button>
              <Pill tone={r.status === "Response Shared" ? "success" : "warn"}>{r.status}</Pill>
            </div>
          </div>

          <QueryPanel interest={r} bank={bank} onSent={onRefresh} />
        </Card>
      ))}
    </div>
  );
}

/* ---------- Watchlist tab (embedded copy of /investor/watchlist) ---------- */
function WatchlistTab({ onDropCount }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/investor/watchlist");
      // API returns {items:[...], total:n}. Older shape was a bare array;
      // handle both defensively.
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setRows(items);
      // Trust delta lives on `delta` (backend field name), not
      // trust_score_delta_7d. Fall back to either.
      const drops = items.filter((it) => (
        (typeof it.delta === "number" ? it.delta : (it.trust_score_delta_7d || 0))
      ) <= -5).length;
      onDropCount && onDropCount(drops);
    } catch { toast.error("Failed to load watchlist"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    try { await api.delete(`/investor/watchlist/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Remove failed"); }
  };
  if (loading) return <div className="text-sm text-ink-soft italic">Loading watchlist…</div>;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Watchlist is empty"
        hint="Add startups you want to quietly track from any startup detail page."
      />
    );
  }
  return (
    <div className="space-y-3" data-testid="watchlist-list">
      {rows.map((it) => {
        const delta = typeof it.delta === "number" ? it.delta : (it.trust_score_delta_7d || 0);
        const trustScore = it.trust_score || it.current_trust?.total || "—";
        return (
          <Card key={it.startup_id} testId={`watchlist-row-${it.startup_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <Link
                  to={`/investor/startup/${it.startup_id}`}
                  className="font-heading text-ink text-lg hover:text-[#8A6600]"
                  data-testid={`watchlist-link-${it.startup_id}`}
                >{it.startup_name || it.startup_id}</Link>
                <div className="text-[11px] font-mono uppercase text-ink-muted tracking-wider mt-1">
                  {it.sector || "—"} · {it.business_stage || "—"} · Added {it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone={delta > 0 ? "success" : delta < 0 ? "danger" : "muted"}>
                  {delta > 0 ? <TrendingUp size={11} className="inline mr-1" /> : delta < 0 ? <TrendingDown size={11} className="inline mr-1" /> : null}
                  Trust {trustScore} {delta ? `(${delta > 0 ? "+" : ""}${delta})` : ""}
                </Pill>
                <button
                  onClick={() => remove(it.startup_id)}
                  className="text-xs text-rose-700 hover:underline"
                  data-testid={`watchlist-remove-${it.startup_id}`}
                >Remove</button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

/* ---------- Page shell with tabs ---------- */
export default function InvestorInterests() {
  const loc = useLocation();
  const nav = useNavigate();
  const initialTab = new URLSearchParams(loc.search).get("tab") === "watchlist" ? "watchlist" : "interests";
  const [tab, setTab] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [bank, setBank] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [watchDrops, setWatchDrops] = useState(0);

  useEffect(() => {
    const t = new URLSearchParams(loc.search).get("tab");
    if (t === "watchlist" && tab !== "watchlist") setTab("watchlist");
    if (!t && tab !== "interests") setTab("interests");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  const changeTab = (id) => {
    setTab(id);
    nav(id === "watchlist" ? "?tab=watchlist" : "", { replace: true });
  };

  const load = async () => {
    setRows((await api.get("/investor/interests")).data || []);
    setBank((await api.get("/query-bank")).data || []);
    // Preload watchlist to compute the red-dot drop count without waiting
    // for the user to actually visit the Watchlist tab.
    try {
      const w = (await api.get("/investor/watchlist")).data;
      const items = Array.isArray(w) ? w : (w?.items || []);
      setWatchDrops(items.filter((it) => (
        (typeof it.delta === "number" ? it.delta : (it.trust_score_delta_7d || 0))
      ) <= -5).length);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const tabs = [
    { id: "interests", label: "My Interests", count: rows.length },
    { id: "watchlist", label: "Watchlist" },
  ];

  return (
    <DashboardShell title="My Interests" subtitle="INVESTOR · CONVERSATIONS & QUIET TRACKING">
      <Toaster richColors position="top-right" />

      {/* Tab bar */}
      <div className="mb-5 flex items-center gap-1 border-b border-navy/10" data-testid="interests-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => changeTab(t.id)}
            className={`px-4 py-2.5 text-sm font-heading transition-colors border-b-2 -mb-px relative ${
              tab === t.id
                ? "border-[#8A6600] text-[#8A6600]"
                : "border-transparent text-ink-soft hover:text-ink"
            }`}
            data-testid={`interests-tab-${t.id}`}
          >
            {t.label} {typeof t.count === "number" && <span className="text-[10px] font-mono ml-1 text-ink-faint">· {t.count}</span>}
            {t.id === "watchlist" && watchDrops > 0 && (
              <span
                className="absolute top-1.5 right-1 inline-block w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white animate-pulse"
                title={`${watchDrops} tracked startup${watchDrops === 1 ? "" : "s"} dropped 5+ trust pts this week`}
                data-testid="watchlist-tab-drop-dot"
              />
            )}
          </button>
        ))}
      </div>

      {tab === "interests" && (
        <InterestsTab
          rows={rows}
          bank={bank}
          onNotProceeding={(r) => setRejecting({ interest: r })}
          onRefresh={load}
        />
      )}
      {tab === "watchlist" && <WatchlistTab onDropCount={setWatchDrops} />}

      <RejectionReasonModal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        context="interest"
        targetId={rejecting?.interest?.interest_id}
        startupId={rejecting?.interest?.startup_id}
        startupName={rejecting?.interest?.startup_name}
        onSubmitted={() => { setRejecting(null); load(); }}
      />
    </DashboardShell>
  );
}
